import { styled } from '@linaria/react';
import { useEffect, useRef } from 'react';
import type { AgentMinerSessionDetail } from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight } from '../../../styles/design-tokens';
import { TurnCard } from './turn-card';

type SessionTurnsProps = {
  session: AgentMinerSessionDetail;
  onTurnOpen?: () => void;
  /**
   * Turn to open and scroll to on mount, e.g. from a `?turn=` link. When it
   * matches no turn the list falls back to opening the first one.
   */
  focusTurnId?: string | null;
};

/**
 * Renders a session's transcript as a list of collapsible turn cards. The first
 * turn starts open, unless `focusTurnId` names another turn to open and scroll to.
 *
 * @param {SessionTurnsProps} props - The session, an open callback, and an optional turn to focus
 * @returns {JSX.Element} The transcript list
 */
export function SessionTurns({ session, onTurnOpen, focusTurnId = null }: SessionTurnsProps) {
  const turns = session.turns ?? [];
  const childSessions = new Map((session.child_sessions ?? []).map((child) => [child.id, child]));
  const focusIndex = focusTurnId ? turns.findIndex((turn) => turn.id === focusTurnId) : -1;
  const openIndex = focusIndex >= 0 ? focusIndex : 0;
  const focusRef = useRef<HTMLDivElement>(null);

  // Scroll once the focused card exists. `scrollIntoView` is optional-called
  // because jsdom does not implement it.
  useEffect(() => {
    if (focusIndex >= 0) focusRef.current?.scrollIntoView?.({ block: 'start' });
  }, [focusIndex]);
  return (
    <Container>
      <Header>
        <HeaderLabel>Turns · {turns.length}</HeaderLabel>
      </Header>
      {turns.length === 0 ? (
        <Empty>No turns recorded yet.</Empty>
      ) : (
        turns.map((turn, index) => {
          const card = (
            <TurnCard
              key={turn.id}
              turn={turn}
              index={index}
              total={turns.length}
              defaultOpen={index === openIndex}
              onOpen={onTurnOpen}
              childSessions={childSessions}
            />
          );
          // Only the focused card gets a wrapper, so the list's layout is unchanged otherwise.
          return index === focusIndex ? (
            <FocusAnchor key={turn.id} ref={focusRef}>
              {card}
            </FocusAnchor>
          ) : (
            card
          );
        })
      )}
    </Container>
  );
}

const FocusAnchor = styled.div`
  scroll-margin-top: 16px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLabel = styled.div`
  font: 600 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Empty = styled.div`
  font: 400 12.5px/1.4 'Figtree', sans-serif;
  color: ${inkLight};
  padding: 12px 0;
`;
